import React, {useState, useEffect, useRef} from 'react';
import { useSelector } from 'react-redux';
import { Form, Input, DatePicker, Select, message, Space, Button } from 'antd';
import { useWindowWidth } from '@react-hook/window-size';
import {qtyClients, cartToReserved} from '../../extensions/helper';
import { serverFetch } from '../../server';
import { HtmlDescription } from '../../extensions/utils';
import moment from 'moment';
import dayjs from 'dayjs';

const isPastDate = (current) => current && current < dayjs().startOf('day');

const getDefaultBookingDate = (bookFrom, bookTo) => {
    let date = dayjs().startOf('day');
    if (bookFrom) {
        const from = dayjs(bookFrom).startOf('day');
        if (date.isBefore(from, 'day')) date = from;
    }
    if (bookTo) {
        const to = dayjs(bookTo).startOf('day');
        if (date.isAfter(to, 'day')) date = to;
    }
    if (isPastDate(date) && !bookFrom) {
        return dayjs(moment().utcOffset(0, true).set({ hour: 24, minute: 0, second: 0, millisecond: 0 }));
    }
    return date;
};

const Booking = ({ item, setCurrentItem, setCurrentRents, fixedPrice, fixedQtyClients, bookFrom, bookTo }) => {
    const { Option } = Select;  
    const company = useSelector((s) => s.company);
    const user = useSelector((s) => s.user);
    const cartItems = useSelector((s) => s.cart?.items || []);
    const [form] = Form.useForm();
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [times, setTimes] = useState([]);
    const [seansTovs, setSeansTovs] = useState([]);
    const [rentItems, setRentItems] = useState([]);
    const [product, setProduct] = useState(null);
    const roomsRef = useRef([]);
    const timesRef = useRef([]);
    const updateRooms = (list) => { roomsRef.current = list; setRooms(list); };
    const updateTimes = (list) => { timesRef.current = list; setTimes(list); };
    const onlyWidth = useWindowWidth();
    const isMobile = onlyWidth < 744;

    const isDateDisabled = (current) => {
        if (!current) return false;
        if (isPastDate(current)) return true;
        if (bookFrom && current.isBefore(dayjs(bookFrom).startOf('day'), 'day')) return true;
        if (bookTo && current.isAfter(dayjs(bookTo).startOf('day'), 'day')) return true;
        return false;
    };

    useEffect(() => {
        form.setFieldsValue({
            qtyClients: fixedQtyClients ?? 1,
            date: getDefaultBookingDate(bookFrom, bookTo),
            rent: false,
        });
        if (company) {
            let cleanup = false;
            const fetchData = async () => {
                setLoading(true);
                let queryParams = {
                    companyId: company.id,
                    isActive: true,
                    productId: item.productId
                };
                let promises = [
                    await serverFetch('room', { method: 'GET', queryParams: queryParams }, user)
                        .then((data) => {
                            return data;
                        })
                        .catch((e) => {
                            message.error('Ошибка чтения залов');
                            console.log(e);
                            setLoading(false);
                        }),
                ];
                Promise.all([promises]).then((result) => {
                    if (cleanup) return;
                    const roomsList = result[0][0] || [];
                    updateRooms(roomsList);
                    if (roomsList.length > 0) {
                        const roomId = roomsList[0].id;
                        form.setFieldsValue({ roomId });
                        getTimes(roomId);
                    } else {
                        updateTimes([]);
                        form.setFieldsValue({ timeId: null });
                    }
                    setLoading(false);
                });
            };
            fetchData();
            return () => (cleanup = true);
        }
    }, [company?.id, item.productId]);

    useEffect(() => {
        // Загружаем product
        if (item && item.productId) {
            serverFetch(`product/${item.productId}`, { method: 'GET' }, user)
                .then((data) => {
                    setProduct(data);
                })
                .catch((e) => {
                    console.log('Ошибка загрузки продукта:', e);
                });
        }
    }, [item, user]);

    const onValuesChange = (value) => {
        console.log(value);
        if (value.roomId || value.date || value.qtyClients) getTimes();
        calcPrices();
    }

    const getTimes = (roomIdOverride) => {
        const sale = form.getFieldsValue();
        const roomId = roomIdOverride ?? sale.roomId;
        if (!roomId) {
            updateTimes([]);
            form.setFieldsValue({ timeId: null });
            return;
        }
        let queryParams = {
            companyId: company.id,
            roomId,
            qtyClients: sale.qtyClients,
            date: dayjs(sale.date).format("YYYY-MM-DD"),
            productId: item.productId
        };
        serverFetch('room/times', { method: 'POST', bodyData: { ...queryParams, cart: cartToReserved(cartItems, company.id) } }, user)
            .then((data) => {
                // console.log("getTimes->", data);
                updateTimes(data);
                const currentTimeId = sale.timeId;
                if (data.length === 0) { 
                    form.setFieldsValue({timeId: null}); 
                    calcPrices();
                }
                else if (!data.find(el => el.id === currentTimeId) ) {
                    form.setFieldsValue({timeId: data[0].id});
                    calcPrices();
                    getSeansTov();
                }
                setLoading(false);
            })
            .catch((e) => {
                message.error('Ошибка получение интервалов');
                console.log(e);
                setLoading(false);
            })
    }

    const getSeansTov = () => {
        const roomSeansId = form.getFieldValue("timeId");
        if (item.productDuration == 0 && roomSeansId) {
            serverFetch(`room/SeansProducts/${roomSeansId}`, { method: 'GET'}, user)
                .then((data) => {
                    setSeansTovs(data);
                })
                .catch((e) => {
                    message.error('Ошибка получение услуг сеанса');
                    console.log(e);
                    setLoading(false);
                });
        }
        else setSeansTovs([]);
    }

    const calcPrices = (items = rentItems) => {
        setCurrentRents(items);
        const timeId = form.getFieldValue('timeId');
        const incompleteRent = items.find(i => !i.productId || !i.size);
        const roomId = form.getFieldValue('roomId');
        const buildCurrentItem = (totalPrice, commission, extra = {}) => ({
            productId: item.productId,
            qty: form.getFieldValue('qtyClients'),
            price: 0,
            totalPrice,
            commission,
            qtyClients: form.getFieldValue('qtyClients'),
            startMin: timeId,
            date: dayjs(form.getFieldValue('date')).format("YYYY-MM-DD"),
            roomId,
            roomName: roomsRef.current.find(r => r.id === roomId)?.name,
            timeName: timesRef.current.find(t => t.id === timeId)?.name,
            catalogId: item.id,
            extraCommission: extra.extraCommission ?? null,
            extraTotal: extra.extraTotal ?? null,
        });

        if (fixedPrice != null) {
            const canPay = timeId != null && !incompleteRent;
            form.setFieldValue('totalPrice', canPay ? fixedPrice : null);
            form.setFieldValue('commission', canPay ? 0 : null);
            setCurrentItem(buildCurrentItem(canPay ? fixedPrice : null, canPay ? 0 : null));
            return;
        }

        // Получение стоимости и комиссии
        // Если не заполнен прокат то не пытаемся считать
        if (incompleteRent){
            form.setFieldValue('totalPrice', null);
            form.setFieldValue('commission', null);
            setCurrentItem(buildCurrentItem(null, null));
        }
        else {
            const formObj = form.getFieldsValue();
            const queryParams = {
                companyId: company.id,
                qtyClients: formObj.qtyClients,
                date: formObj.date.format("YYYY-MM-DD"),
                productId: item.productId,
                catalogId: item.id,
                startMin: form.getFieldValue('timeId') ?? 0,
                rentProductIds: items.map(i => i.productId),
            };
            form.setFieldValue('totalPrice', null);
            serverFetch('sale/getprice', { method: 'POST', bodyData: queryParams}, user)
                .then((data) => {
                    form.setFieldValue('totalPrice', data.price);
                    form.setFieldValue('commission', data.commission);
                    const hasTime = form.getFieldValue('timeId') != null;
                    setCurrentItem(buildCurrentItem(hasTime ? data.price : null, hasTime ? data.commission : null, {
                        extraCommission: data.extraCommission,
                        extraTotal: hasTime ? (data.extraPrice || 0) + (data.extraCommission || 0) : null,
                    }));
                })
                .catch((e) => {
                    message.error('Ошибка получение стоимости');
                    console.log(e);
                })
        }
    }

    // #region Прокат
    let rentTovs = [];
    seansTovs.forEach(i => {
        if (i.qty > 0 && !rentTovs.find(j => j.value == i.productId))
            rentTovs.push({value: i.productId, label: i.productName, key: i.productId });
    })
    
    const formObj = form.getFieldsValue();
    
    // Команды проката
    const onRentAdd = () => {
        const newA = [...rentItems, {}];
        setRentItems(newA);
        calcPrices(newA);
    }
    const onRentDel = async () => {
        let newA = [...rentItems];
        if (newA.length > 0) {
            newA.pop();
            setRentItems(newA);
            calcPrices(newA);
        }
    }
    const onRentProductSelect = (idx, value) => {
        const newRentItems = [...rentItems];
        newRentItems[idx].productId = value;
        setRentItems(newRentItems);
        onRentSizeSelect(idx, null);
    }

    const onRentSizeSelect = (idx, value) => {
        const newRentItems = [...rentItems];
        newRentItems[idx].size = value;
        setRentItems(newRentItems);
        calcPrices();
    }

    // Выбран прокат
    const rent = rentItems.length > 0;

    const qtyClientsOptions = fixedQtyClients != null && !qtyClients.includes(fixedQtyClients)
        ? [...qtyClients, fixedQtyClients].sort((a, b) => a - b)
        : qtyClients;

    // Есть оборудование для проката
    const enableRent = formObj.date && formObj.timeId && formObj.roomId && seansTovs.filter(d => d.qty > 0).length > 0;

    const divRents = rentItems.map((i,idx) => {
        const rentSizes = seansTovs.filter(i2 => i2.qty > 0 && i2.productId == i.productId).map(i2 => {return {value: i2.size, label: i2.size, key: i2.size}});
        return <Form.Item label={`Инв. ${idx + 1}`} key={`rent_${idx}`} >
            <div style={{ display: 'flex', gap: 8 }}>
                <Form.Item hasFeedback validateStatus={i.productId ? '' : 'error'} style={{ flex: 1, marginBottom: 0 }}><Select style={{width: '100%'}} options={rentTovs} onSelect={(value) => onRentProductSelect(idx,value)} placeholder="Оборудование"/></Form.Item>
                <Form.Item label="Размер" hasFeedback validateStatus={i.size ? '' : 'error'} style={{ marginBottom: 0 }}><Select style={{width: 85}} options={rentSizes} onSelect={(value) => onRentSizeSelect(idx,value)}/></Form.Item>
            </div>
        </Form.Item>
    })

    const divRent = enableRent && <>
        <Form.Item label="Прокат">
            <Space>
                <Button onClick={onRentAdd}>Добавить</Button>
                <Button onClick={onRentDel}>Удалить</Button>
            </Space>
        </Form.Item>
        {divRents}
    </>

    //console.log(rentItems);
    // #endregion

    return (
        <>
            <div style={{ marginTop: 16, marginBottom: 8 }}><HtmlDescription value={product?.description} /></div>
            <Form colon={false} layout={isMobile ? 'vertical' : 'horizontal'} labelCol={isMobile ? undefined : { span: 7 }} wrapperCol={isMobile ? undefined : { span: 17 }} form={form} onValuesChange={onValuesChange}>
                <Form.Item label='Зал' name="roomId">
                        <Select placeholder='Выберите зал' disabled={loading || rent}>
                            {rooms.map((p) => <Option key={`room}_${p.id}`} value={p.id}>{p.name}</Option>)}
                        </Select>
                </Form.Item>
                <Form.Item label='Посетителей' wrapperCol={{span: 6}} min={1} max={10} name="qtyClients">
                    <Select disabled={loading || !item.productDuration || rent || fixedQtyClients != null}>
                            {qtyClientsOptions.map((p) => <Option key={`time}_${p}`} value={p}>{p}</Option>)}
                    </Select>
                </Form.Item>
                <Form.Item label='Дата' name="date">
                    <DatePicker
                        style={{ width: '100%' }}
                        allowClear={false}
                        format="DD.MM.YYYY"
                        disabledDate={isDateDisabled}
                        disabled={rent}
                        popupStyle={{ zIndex: 2100 }}
                        getPopupContainer={() => document.body}
                    />
                </Form.Item>
                <Form.Item label='Время' name="timeId">
                        <Select placeholder='Выберите время' disabled={loading || rent}>
                            {times.map((p) => <Option key={`time}_${p.id}`} value={p.id}>{p.name}</Option>)}
                        </Select>
                </Form.Item>
                {divRent}
                <Form.Item noStyle shouldUpdate={(prev, next) => prev.commission !== next.commission}>
                    {() => {
                        const commission = form.getFieldValue('commission');
                        if (!commission) return null;
                        return (
                            <Form.Item label='Комиссия' wrapperCol={{ span: 6 }} name='commission'>
                                <Input size='middle' disabled suffix='₽' />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            </Form>
        </>
    );
};

export default Booking;